/**
 * https://www.chromium.org/updates/same-site/incompatible-clients
 * 
 * Some user agents are known to be incompatible with the `SameSite=None` attribute.
 * 
 * Versions of Chrome from Chrome 51 to Chrome 66 (inclusive on both ends).
 * These Chrome versions will reject a cookie with `SameSite=None`. This also affects older versions of Chromium-derived browsers, as well as Android WebView.
 * This behavior was correct according to the version of the cookie specification at that time, but with the addition of the new "None" value to the
 * specification, this behavior has been updated in Chrome 67 and newer. (Prior to Chrome 51, the SameSite attribute was ignored entirely and all cookies were
 * treated as if they were `SameSite=None`.)
 * 
 * Versions of UC Browser on Android prior to version 12.13.2. Older versions will reject a cookie with
 * `SameSite=None`. This behavior was correct according to the version of the cookie specification at that time, but with the addition of the new "None" value
 * to the specification, this behavior has been updated in newer versions of UC Browser.
 * 
 * Versions of Safari and embedded browsers on MacOS 10.14 and all
 * browsers on iOS 12. These versions will erroneously treat cookies marked with `SameSite=None` as if they were marked `SameSite=Strict`. This bug has been
 * fixed on newer versions of iOS and MacOS.
 *
 * @author ryan.nguyen
 */

const IOS_MAJOR_VERSION = 12;
const MACOSX_MAJOR_VERSION = 10;
const MACOSX_MINOR_VERSION = 14;
const UC_MAJOR_VERSION_MIN = 12;
const UC_MINOR_VERSION_MIN = 13;
const UC_BUILD_NO_MIN = 2;
const CHROMIUM_MAJOR_VERSION_MIN = 51;
const CHROMIUM_MAJOR_VERSION_MAX = 67;
let isSameSiteCompatibleFlag = null;

function isSameSiteNoneIncompatible(userAgent) {
    return hasWebKitSameSiteBug(userAgent) ||
        dropsUnrecognizedSameSiteCookies(userAgent);
}

function hasWebKitSameSiteBug(userAgent) {
    return isIosVersion(IOS_MAJOR_VERSION, userAgent) ||
        (isMacosxVersion(MACOSX_MAJOR_VERSION, MACOSX_MINOR_VERSION, userAgent) &&
            (isSafari(userAgent) || isMacEmbeddedBrowser(userAgent)));
}

function dropsUnrecognizedSameSiteCookies(userAgent) {
    if (isUcBrowser(userAgent)) {
        return !isUcBrowserVersionAtLeast(UC_MAJOR_VERSION_MIN, UC_MINOR_VERSION_MIN, UC_BUILD_NO_MIN, userAgent);
    }
    return isChromiumBased(userAgent) &&
        isChromiumVersionAtLeast(CHROMIUM_MAJOR_VERSION_MIN, userAgent) &&
        !isChromiumVersionAtLeast(CHROMIUM_MAJOR_VERSION_MAX, userAgent);
}

function isIosVersion(major, userAgent) {
    let regex = /\(iP.+; CPU .*OS (\d+)[_\d]*.*\) AppleWebKit\//;
    // Extract digits from first capturing group.
    let matches = regex.exec(userAgent);
    if (matches && matches.length > 1) {
        return matches[1] == major;
    }
    return false;
}

function isMacosxVersion(major, minor, userAgent) {
    let regex = /\(Macintosh;.*Mac OS X (\d+)_(\d+)[_\d]*.*\) AppleWebKit\//;
    // Extract digits from first and second capturing groups.
    let matches = regex.exec(userAgent);
    if (matches && matches.length > 2) {
        return major == matches[1] &&
            minor == matches[2];
    }
    return false;
}

function isSafari(userAgent) {
    let regex = /Version\/.* Safari\//;
    return regex.test(userAgent) &&
        !isChromiumBased(userAgent);
}

function isMacEmbeddedBrowser(userAgent) {
    let regex = /^Mozilla\/[\.\d]+ \(Macintosh;.*Mac OS X [_\d]+\) AppleWebKit\/[\.\d]+ \(KHTML, like Gecko\)$/;
    return regex.test(userAgent);
}

function isChromiumBased(userAgent) {
    let regex = /Chrom(e|ium)/;
    return regex.test(userAgent);
}

function isChromiumVersionAtLeast(major, userAgent) {
    let regex = /Chrom[^ \/]+\/(\d+)[\.\d]* /;
    // Extract digits from first capturing group.
    let matches = regex.exec(userAgent);
    if (matches && matches.length > 1) {
        let version = matches[1];
        return version >= major;
    }
    return false;
}

function isUcBrowser(userAgent) {
    let regex = /UCBrowser\//;
    return regex.test(userAgent);
}

function isUcBrowserVersionAtLeast(major, minor, build, userAgent) {
    let regex = /UCBrowser\/(\d+)\.(\d+)\.(\d+)[\.\d]* /;
    // Extract digits from three capturing groups.
    let matches = regex.exec(userAgent);
    if (matches && matches.length > 3) {
        let majorVersion = matches[1];
        let minorVersion = matches[2];
        let buildVersion = matches[3];

        if (majorVersion != major) {
            return majorVersion > major;
        }
        if (minorVersion != minor) {
            return minorVersion > minor;
        }
        return buildVersion >= build;
    }
    return false;
}

export default class SameSite {

    static isSameSiteCompatible() {
        if (isSameSiteCompatibleFlag == null) {
            isSameSiteCompatibleFlag = !isSameSiteNoneIncompatible(navigator.userAgent);
        }

        return isSameSiteCompatibleFlag;
    }

}


// WEBPACK FOOTER //
// ./src/app/common/SameSite.js